/**
 * Set of generic interfaces to allow PowerSync compatibility with
 * different SQLite DB implementations.
 */
import { BaseObserver } from '../utils/BaseObserver.js';
import { queryResultFromRaw, queryResultWithoutRows } from './QueryResult.js';
/**
 * @public
 */
export class LockContext {
    async getAll(sql, parameters) {
        const rs = await this.execute(sql, parameters);
        return Array.from(rs);
    }
    async getOptional(sql, parameters) {
        const { array } = await this.execute(sql, parameters);
        if (array.length > 0) {
            return array[0];
        }
        return null;
    }
    async get(sql, parameters) {
        const row = await this.getOptional(sql, parameters);
        if (row == null) {
            throw new Error('Result set is empty');
        }
        return row;
    }
    async execute(query, params) {
        const raw = await this.executeRaw(query, params);
        return queryResultFromRaw(raw);
    }
    async executeBatch(query, params = []) {
        // Emulate executeBatch by running statements individually.
        let lastInsertId;
        let rowsAffected = 0;
        for (const set of params) {
            const result = await this.execute(query, set);
            lastInsertId = result.insertId;
            rowsAffected += result.rowsAffected ?? 0;
        }
        return queryResultWithoutRows({
            rowsAffected,
            insertId: lastInsertId
        });
    }
}
/**
 * @public
 */
export class DBAdapter extends BaseObserver {
    readTransaction(fn, options) {
        return this.readLock((ctx) => TransactionImplementation.runWith(ctx, fn), options);
    }
    writeTransaction(fn, options) {
        return this.writeLock((ctx) => TransactionImplementation.runWith(ctx, fn), options);
    }
    getAll(sql, parameters) {
        return this.readLock((ctx) => ctx.getAll(sql, parameters));
    }
    getOptional(sql, parameters) {
        return this.readLock((ctx) => ctx.getOptional(sql, parameters));
    }
    get(sql, parameters) {
        return this.readLock((ctx) => ctx.get(sql, parameters));
    }
    execute(query, params) {
        return this.writeLock((ctx) => ctx.execute(query, params));
    }
    executeRaw(query, params) {
        return this.writeLock((ctx) => ctx.executeRaw(query, params));
    }
    executeBatch(query, params) {
        return this.writeTransaction((tx) => tx.executeBatch(query, params));
    }
}
class TransactionImplementation extends LockContext {
    inner;
    finalized = false;
    constructor(inner) {
        super();
        this.inner = inner;
    }
    async commit() {
        if (this.finalized) {
            return;
        }
        this.finalized = true;
        await this.inner.execute('COMMIT');
    }
    async rollback() {
        if (this.finalized) {
            return;
        }
        this.finalized = true;
        await this.inner.execute('ROLLBACK');
    }
    execute(query, params) {
        return this.inner.execute(query, params);
    }
    executeRaw(query, params) {
        return this.inner.executeRaw(query, params);
    }
    executeBatch(query, params) {
        return this.inner.executeBatch(query, params);
    }
    static async runWith(ctx, fn) {
        let tx = new TransactionImplementation(ctx);
        try {
            await ctx.execute('BEGIN IMMEDIATE');
            const result = await fn(tx);
            await tx.commit();
            return result;
        }
        catch (ex) {
            try {
                await tx.rollback();
            }
            catch (ex2) {
                // In rare cases, a rollback may fail.
                // Safe to ignore.
            }
            throw ex;
        }
    }
}
//# sourceMappingURL=DBAdapter.js.map